import * as React from "react";
import { HoloShimmer } from "@/components/Holo";
import { creatingLabel } from "@/components/Backgrounds/BackgroundPicker";
import { TIER_LABELS } from "@/components/Backgrounds/facetLabels";
import { BackgroundTileCrop } from "@/components/Backgrounds/BackgroundTileCrop";
import { aspectClass } from "@/components/Backgrounds/ratios";
import { useBackgroundLibrary } from "@/components/Backgrounds/useBackgroundLibrary";
import { Badge, Button, Checkbox, Skeleton, Tooltip } from "@/components/ui";
import { useInfiniteSentinel } from "@/containers/Coach/PromptStudio/hooks/useInfiniteSentinel";
import { cn } from "@/lib/utils";
import { BackgroundAsset, BackgroundRequest, BackgroundSearchBody } from "@/services/bedrock/dtos/BackgroundAsset";
import { PreviewRatio } from "./LibraryToolbar";

export type AssetSelection = Map<number, BackgroundAsset>;

interface AssetGridProps {
  body: BackgroundSearchBody;
  previewRatio: PreviewRatio;
  selected: AssetSelection;
  onSelectedChange(selected: AssetSelection): void;
  onOpen(assetId: number): void;
  pendingRequests: BackgroundRequest[];
}

const SKELETON_TILES = 12;

const VisibilityBadge: React.FC<{ asset: BackgroundAsset }> = ({ asset }) => {
  if (asset.visibility === "draft") {
    return (
      <Badge size="small" className="absolute top-1 right-1 border-transparent bg-amber-100 text-amber-800">
        Draft
      </Badge>
    );
  }
  if (asset.visibility === "archived") {
    return (
      <Badge size="small" className="absolute top-1 right-1 border-transparent bg-slate-200 text-slate-700">
        Archived
      </Badge>
    );
  }
  return null;
};

const TierDot: React.FC<{ asset: BackgroundAsset }> = ({ asset }) => {
  if (!asset.tier) return null;
  const label = `${TIER_LABELS[asset.tier]} tier`;
  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <span
            className={cn(
              "absolute bottom-1.5 right-1.5 size-2.5 rounded-full ring-2 ring-white",
              asset.tier === "quality" ? "bg-violet-500" : "bg-sky-500"
            )}
            data-testid="tier-dot"
            aria-label={label}
          />
        }
      />
      <Tooltip.Content>{label}</Tooltip.Content>
    </Tooltip>
  );
};

/** The library grid for curation: click opens an asset, the checkbox (or shift-click) selects. */
export const AssetGrid: React.FC<AssetGridProps> = ({
  body,
  previewRatio,
  selected,
  onSelectedChange,
  onOpen,
  pendingRequests,
}) => {
  const { assets, fetchNextPage, hasNextPage, isLoading, isFetching, isError, refetch } = useBackgroundLibrary(body);
  const anchor = React.useRef<number | null>(null);
  const shiftHeld = React.useRef(false);

  const sentinelRef = useInfiniteSentinel({
    enabled: !!hasNextPage && !isFetching,
    onIntersect: () => void fetchNextPage(),
  });

  const toggle = (asset: BackgroundAsset) => {
    const next = new Map(selected);
    if (next.has(asset.id)) next.delete(asset.id);
    else next.set(asset.id, asset);
    anchor.current = asset.id;
    onSelectedChange(next);
  };

  // Everything between the last tile picked and this one joins the selection.
  const extend = (asset: BackgroundAsset) => {
    const from = assets.findIndex(a => a.id === anchor.current);
    const to = assets.findIndex(a => a.id === asset.id);
    if (from === -1) return toggle(asset);
    const next = new Map(selected);
    assets.slice(Math.min(from, to), Math.max(from, to) + 1).forEach(a => next.set(a.id, a));
    anchor.current = asset.id;
    onSelectedChange(next);
  };

  if (isError) {
    return (
      <p className="text-sm text-slate-500">
        We couldn't load backgrounds.{" "}
        <Button variant="link" size="inline" onClick={() => void refetch()}>
          Try again
        </Button>
      </p>
    );
  }

  if (!isLoading && assets.length === 0 && pendingRequests.length === 0) {
    return <p className="text-sm text-slate-500">Nothing here. Create some with AI or change the filters.</p>;
  }

  return (
    <>
      <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(140px,1fr))]" data-testid="asset-grid">
        {pendingRequests.map(request => (
          <HoloShimmer
            key={`request-${request.id}`}
            aspectRatio={previewRatio}
            label={creatingLabel(request.estimatedWaitSeconds)}
            className="rounded-lg"
          />
        ))}
        {isLoading
          ? Array.from({ length: SKELETON_TILES }, (_, i) => (
              <Skeleton key={`skeleton-${i}`} className={cn("w-full rounded-lg", aspectClass(previewRatio))} />
            ))
          : assets.map(asset => {
              const isSelected = selected.has(asset.id);
              return (
                <div
                  key={asset.id}
                  className="relative"
                  data-testid="asset-tile"
                  // Read on press, not click: the checkbox re-dispatches its own click without the modifier.
                  onMouseDownCapture={event => {
                    shiftHeld.current = event.shiftKey;
                  }}
                  onKeyDownCapture={event => {
                    shiftHeld.current = event.shiftKey;
                  }}
                >
                  <BackgroundTileCrop
                    asset={asset}
                    ratio={previewRatio}
                    selected={isSelected}
                    onClick={() => (shiftHeld.current ? extend(asset) : onOpen(asset.id))}
                    badge={
                      <>
                        <VisibilityBadge asset={asset} />
                        <TierDot asset={asset} />
                      </>
                    }
                  />
                  <span className="absolute top-1.5 left-1.5 rounded bg-white/90 p-0.5 shadow-sm">
                    <Checkbox
                      checked={isSelected}
                      aria-label={`Select ${asset.description ?? "background"}`}
                      onCheckedChange={() => (shiftHeld.current ? extend(asset) : toggle(asset))}
                    />
                  </span>
                </div>
              );
            })}
      </div>
      <div ref={sentinelRef} aria-hidden="true" />
    </>
  );
};
